import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { DragEvent } from "react";
import "../../photo-gallery.css";
import type { ReportAsset, WorkAssignment } from "../types/api";

type WorkWithPhotos = {
  work: WorkAssignment;
  photos: ReportAsset[];
};

type GalleryEntry = {
  asset: ReportAsset;
  work: WorkAssignment;
};

type Props = {
  workItems: WorkWithPhotos[];
  allWorkAssignments: WorkAssignment[];
  assetUrls: Map<string, string>;
  assetPreviewUrls: Map<string, string>;
  archiveUnavailablePaths: Set<string>;
  editable: boolean;
  busy?: boolean;
  onMove: (asset: ReportAsset, targetWorkAssignmentId: string) => Promise<void>;
};

function assetKey(asset: ReportAsset): string {
  return asset.report_asset_id || asset.storage_path || asset.filename || "foto";
}

function safeFilename(value: string | undefined, fallback = "foto.jpg"): string {
  const original = String(value || "");
  if (original.startsWith("image_sediment:")) return fallback;
  const raw = original.split(/[\\/]/).pop() || fallback;
  const cleaned = raw.replace(/[<>:"/\\|?*]/g, "_").trim();
  return cleaned || fallback;
}

function mimeFromFilename(filename: string): string {
  const extension = filename.split(".").pop()?.toLowerCase();
  return ({
    png: "image/png",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
  } as Record<string, string>)[extension || ""] || "image/jpeg";
}

function downloadImage(url: string, filename: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function setDragPayload(
  event: DragEvent<HTMLElement>,
  url: string,
  filename: string,
) {
  const transfer = event.dataTransfer;
  transfer.effectAllowed = "copy";
  transfer.setData("text/uri-list", url);
  transfer.setData("text/plain", url);
  transfer.setData("DownloadURL", `${mimeFromFilename(filename)}:${filename}:${url}`);
}

function assignmentLabel(work: WorkAssignment): string {
  const description = work.description?.trim() || "Werkzaamheid zonder omschrijving";
  const workPackage = work.work_package?.trim() || "Niet toegewezen";
  return `${workPackage} · ${description}`;
}

export function WorkPackagePhotoGallery({
  workItems,
  allWorkAssignments,
  assetUrls,
  assetPreviewUrls,
  archiveUnavailablePaths,
  editable,
  busy = false,
  onMove,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const swipeStartX = useRef<number | null>(null);
  const moveTargetId = useId();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [moving, setMoving] = useState(false);

  const entries = useMemo<GalleryEntry[]>(() =>
    workItems.flatMap(({ work, photos }) =>
      [...photos]
        .sort((left, right) => (left.display_order ?? 0) - (right.display_order ?? 0))
        .map((asset) => ({ asset, work }))
    ), [workItems]);

  const active = activeIndex === null ? null : entries[activeIndex] ?? null;
  const activeOriginalUrl = active?.asset.storage_path
    ? assetUrls.get(active.asset.storage_path) ?? null
    : null;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (activeIndex !== null && !dialog.open) dialog.showModal();
    if (activeIndex === null && dialog.open) dialog.close();
  }, [activeIndex]);

  useEffect(() => {
    if (!active) {
      setMoveTarget("");
      return;
    }
    setMoveTarget(active.asset.work_assignment_id || active.work.work_assignment_id || "");
  }, [active?.asset.report_asset_id, active?.work.work_assignment_id]);

  useEffect(() => {
    if (activeIndex === null) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveIndex(null);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveIndex((current) =>
          current === null || entries.length === 0
            ? current
            : (current - 1 + entries.length) % entries.length
        );
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        setActiveIndex((current) =>
          current === null || entries.length === 0 ? current : (current + 1) % entries.length
        );
      }
    };
    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, entries.length]);

  function go(delta: number) {
    setActiveIndex((current) => {
      if (current === null || entries.length === 0) return current;
      return (current + delta + entries.length) % entries.length;
    });
  }

  async function moveActivePhoto() {
    if (!active || !moveTarget || moving) return;
    const currentOwner = active.asset.work_assignment_id || active.work.work_assignment_id || "";
    if (moveTarget === currentOwner) return;
    setMoving(true);
    try {
      await onMove(active.asset, moveTarget);
      setActiveIndex(null);
    } finally {
      setMoving(false);
    }
  }

  if (entries.length === 0) return null;

  return (
    <div className="work-package-photo-groups">
      {workItems.map(({ work, photos }) => {
        if (photos.length === 0) return null;
        return (
          <div className="work-photo-group" key={work.work_assignment_id || work.description}>
            <div className="work-photo-group__heading">
              <strong>{work.description || "Werkzaamheid"}</strong>
              <span className="muted">{photos.length} foto{photos.length === 1 ? "" : "'s"}</span>
            </div>
            <div className="report-photo-grid compact-photo-grid">
              {photos.map((asset) => {
                const index = entries.findIndex((entry) =>
                  assetKey(entry.asset) === assetKey(asset)
                );
                const original = asset.storage_path
                  ? assetUrls.get(asset.storage_path) ?? null
                  : null;
                const preview = asset.storage_path
                  ? assetPreviewUrls.get(asset.storage_path) ?? null
                  : null;
                const imageUrl = preview || original;
                if (!imageUrl) {
                  const archiveUnavailable = Boolean(
                    asset.storage_path && archiveUnavailablePaths.has(asset.storage_path),
                  );
                  return (
                    <span className="badge muted" key={assetKey(asset)}>
                      {archiveUnavailable
                        ? "Dropbox-archief tijdelijk niet bereikbaar"
                        : "Geen preview"}
                    </span>
                  );
                }
                const filename = safeFilename(asset.filename, `foto-${index + 1}.jpg`);
                return (
                  <figure
                    className="report-photo-card compact-photo-card"
                    draggable={Boolean(original)}
                    onDragStart={(event) => {
                      if (original) setDragPayload(event, original, filename);
                    }}
                    key={assetKey(asset)}
                  >
                    <button
                      type="button"
                      className="compact-photo-button"
                      title="Klik voor grotere weergave"
                      onClick={() => setActiveIndex(index)}
                    >
                      <img
                        src={imageUrl}
                        alt={asset.caption || asset.filename || "Foto"}
                        className="report-photo-image compact-photo-image"
                        loading="lazy"
                        decoding="async"
                      />
                    </button>
                    <figcaption className="compact-photo-caption">
                      {asset.caption || asset.filename || "Foto"}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        );
      })}

      <dialog
        ref={dialogRef}
        className="photo-lightbox"
        onClose={() => setActiveIndex(null)}
        onClick={(event) => {
          if (event.target === dialogRef.current) setActiveIndex(null);
        }}
      >
        {active && (
          <div className="photo-lightbox__inner">
            <div className="photo-lightbox__header">
              <div>
                <strong>{active.asset.caption || active.asset.filename || "Foto"}</strong>
                <div className="photo-lightbox__meta">
                  {assignmentLabel(active.work)} · {activeIndex! + 1} van {entries.length}
                </div>
              </div>
              <button
                type="button"
                className="secondary"
                onClick={() => setActiveIndex(null)}
              >
                Terug naar overzicht
              </button>
            </div>

            <div
              className="photo-lightbox__image-wrap"
              onPointerDown={(event) => {
                swipeStartX.current = event.clientX;
              }}
              onPointerUp={(event) => {
                if (swipeStartX.current === null) return;
                const distance = event.clientX - swipeStartX.current;
                swipeStartX.current = null;
                if (Math.abs(distance) < 50) return;
                go(distance > 0 ? -1 : 1);
              }}
            >
              <button
                type="button"
                className="photo-lightbox__nav photo-lightbox__nav--previous"
                onClick={() => go(-1)}
                disabled={entries.length < 2}
                aria-label="Vorige foto"
              >
                ‹
              </button>
              {activeOriginalUrl
                ? (
                  <img
                    src={activeOriginalUrl}
                    alt={active.asset.caption || active.asset.filename || "Foto"}
                    className="photo-lightbox__image"
                    draggable
                    onDragStart={(event) =>
                      setDragPayload(
                        event,
                        activeOriginalUrl,
                        safeFilename(active.asset.filename, `foto-${activeIndex! + 1}.jpg`),
                      )}
                  />
                )
                : (
                  <span className="badge muted">
                    {active.asset.storage_path &&
                        archiveUnavailablePaths.has(active.asset.storage_path)
                      ? "Dropbox-archief tijdelijk niet bereikbaar"
                      : "Geen grote weergave beschikbaar"}
                  </span>
                )}
              <button
                type="button"
                className="photo-lightbox__nav photo-lightbox__nav--next"
                onClick={() => go(1)}
                disabled={entries.length < 2}
                aria-label="Volgende foto"
              >
                ›
              </button>
            </div>

            <div className="photo-lightbox__footer photo-lightbox__footer--actions">
              <div className="photo-lightbox__caption">
                <strong>{active.work.description || "Werkzaamheid"}</strong>
                {active.asset.caption && <span>{active.asset.caption}</span>}
              </div>

              {editable && active.asset.report_asset_id && (
                <div className="photo-move-controls">
                  <label htmlFor={moveTargetId}>Verplaatsen naar werkzaamheid</label>
                  <select
                    id={moveTargetId}
                    value={moveTarget}
                    disabled={busy || moving}
                    onChange={(event) => setMoveTarget(event.target.value)}
                  >
                    {allWorkAssignments
                      .filter((work) => work.work_assignment_id)
                      .map((work) => (
                        <option key={work.work_assignment_id} value={work.work_assignment_id}>
                          {assignmentLabel(work)}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy || moving || !moveTarget ||
                      moveTarget ===
                        (active.asset.work_assignment_id || active.work.work_assignment_id || "")}
                    onClick={() => void moveActivePhoto()}
                  >
                    {moving ? "Verplaatsen..." : "Verplaatsen"}
                  </button>
                </div>
              )}

              <div className="photo-lightbox__action-buttons">
                <button
                  type="button"
                  className="secondary"
                  disabled={entries.length < 2}
                  onClick={() => go(-1)}
                >
                  Vorige
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={entries.length < 2}
                  onClick={() => go(1)}
                >
                  Volgende
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={!activeOriginalUrl}
                  onClick={() => {
                    if (!activeOriginalUrl) return;
                    downloadImage(
                      activeOriginalUrl,
                      safeFilename(active.asset.filename, `foto-${activeIndex! + 1}.jpg`),
                    );
                  }}
                >
                  Foto opslaan
                </button>
              </div>
            </div>
          </div>
        )}
      </dialog>
    </div>
  );
}
